"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isActionAllowedFromStep = exports.canSalesmanAbandon = exports.SALESMAN_ABANDONMENT_STEPS = exports.PROJECT_ACTION_ALLOWED_FROM = exports.stepsExcept = exports.ProjectAction = void 0;
// Progress-scope catalogue for project lifecycle actions.
//
// Each backend project action transitions `projects.step` from one value to
// another. This table is the single source of truth for "which step(s) may a
// given action run from". It is derived from the frontend action gates
// (`frontend/src/components/projects/actions/*`,
// `frontend/src/routes/projects/edit/[id]/synthesis/*`,
// `frontend/src/layouts/ProjectSynthesisLayout.tsx`) and cross-checked against
// the GraphQL transition targets in
// `backend/src/features/project-management/graphql/project-mutations.ts`.
//
// Member-value typing mirrors `steps.ts`: values are typed as the full union so
// `[ProjectAction.X].includes(y)` patterns keep working without per-site casts.
const steps_1 = require("./steps");
const order_1 = require("./order");
const PROJECT_ACTION_LITERAL = {
    REQUEST: 'REQUEST',
    ACCEPT: 'ACCEPT',
    REFUSE: 'REFUSE',
    ORDER: 'ORDER',
    VALIDATE_PAYMENT: 'VALIDATE_PAYMENT',
    START_PROJECT: 'START_PROJECT',
    FILE_COMPLETE: 'FILE_COMPLETE',
    FILE_SEND: 'FILE_SEND',
    FILE_SUBMIT: 'FILE_SUBMIT',
    FILE_REFUSE: 'FILE_REFUSE',
    VALIDATE_COMPLETION: 'VALIDATE_COMPLETION',
    RESUME: 'RESUME',
    ABANDONMENT: 'ABANDONMENT',
    DISABLE: 'DISABLE',
};
exports.ProjectAction = PROJECT_ACTION_LITERAL;
/**
 * All steps except the supplied ones, in canonical order. Used for actions
 * (close/disable) that are valid from anywhere except terminal states — so a
 * future step added to `PROJECT_STEP_ORDER` is automatically included.
 */
const stepsExcept = (excluded) => order_1.PROJECT_STEP_ORDER.filter((s) => !excluded.includes(s));
exports.stepsExcept = stepsExcept;
/**
 * Action → set of project steps the action may run from. A request for an
 * action whose current step is not in this set must be rejected.
 */
exports.PROJECT_ACTION_ALLOWED_FROM = {
    REQUEST: [steps_1.ProjectStep.PROJECT_INITIALIZED, steps_1.ProjectStep.PROJECT_REFUSED],
    // `accept` is dual-purpose: it accepts a freshly REQUESTED project AND backs
    // the architect's "Changer le type de projet" action, which wipes the DP/PC
    // data + CERFA files and resets the project to PROJECT_ACCEPTED. A type change
    // is valid from any working step, so ACCEPT must be allowed from every
    // non-terminal post-request step — never from a not-yet-requested
    // (INITIALIZED/REFUSED) or terminal (COMPLETED/ABANDONED) project.
    ACCEPT: (0, exports.stepsExcept)([
        steps_1.ProjectStep.PROJECT_INITIALIZED,
        steps_1.ProjectStep.PROJECT_REFUSED,
        steps_1.ProjectStep.PROJECT_COMPLETED,
        steps_1.ProjectStep.PROJECT_ABANDONED,
    ]),
    // `refuse` sends a project back to PROJECT_REFUSED ("en attente de
    // modification par le client"). The architect refuses a freshly REQUESTED
    // project; the salesman can also refuse at the type-validation step
    // (PROJECT_ACCEPTED) via "Refuser et modifier" to send the dossier back for
    // editing before the type is committed to drafting.
    REFUSE: [steps_1.ProjectStep.PROJECT_REQUESTED, steps_1.ProjectStep.PROJECT_ACCEPTED],
    ORDER: [steps_1.ProjectStep.PROJECT_ACCEPTED],
    VALIDATE_PAYMENT: [steps_1.ProjectStep.PROJECT_ORDERED],
    START_PROJECT: [
        steps_1.ProjectStep.PROJECT_PAIED,
        steps_1.ProjectStep.PROJECT_FILE_COMPLETED,
        steps_1.ProjectStep.PROJECT_FILE_SENT,
        steps_1.ProjectStep.PROJECT_FILE_SUBMITTED,
        steps_1.ProjectStep.PROJECT_FILE_REFUSED,
    ],
    FILE_COMPLETE: [steps_1.ProjectStep.PROJECT_IN_PROGRESS],
    FILE_SEND: [steps_1.ProjectStep.PROJECT_FILE_COMPLETED],
    FILE_SUBMIT: [
        steps_1.ProjectStep.PROJECT_FILE_SENT,
        steps_1.ProjectStep.PROJECT_FILE_REFUSED,
    ],
    FILE_REFUSE: [steps_1.ProjectStep.PROJECT_FILE_SUBMITTED],
    VALIDATE_COMPLETION: [steps_1.ProjectStep.PROJECT_FILE_SUBMITTED],
    RESUME: [steps_1.ProjectStep.PROJECT_ABANDONED],
    ABANDONMENT: (0, exports.stepsExcept)([
        steps_1.ProjectStep.PROJECT_COMPLETED,
        steps_1.ProjectStep.PROJECT_ABANDONED,
    ]),
    DISABLE: (0, exports.stepsExcept)([
        steps_1.ProjectStep.PROJECT_COMPLETED,
        steps_1.ProjectStep.PROJECT_ABANDONED,
    ]),
};
/**
 * The steps a COMMERCIAL may abandon their own dossier from: everything before
 * the architect starts drafting — INITIALIZED, REQUESTED, REFUSED, ACCEPTED,
 * ORDERED, PAIED.
 *
 * The line is drawn at PROJECT_IN_PROGRESS because that is where somebody
 * else's work begins. Up to there the dossier is the commercial's own intake
 * and a client who changes their mind costs nobody anything; from there the
 * architect is drafting documents that were ordered, and closing that is a
 * decision only they can take. `ABANDONMENT` itself stays open to the architect
 * from every non-terminal step — this narrows WHO, not WHAT.
 *
 * Derived from PROJECT_STEP_ORDER rather than listed, so a step inserted before
 * drafting is covered without a second edit.
 */
exports.SALESMAN_ABANDONMENT_STEPS = order_1.PROJECT_STEP_ORDER.filter((s) => (0, order_1.isStepBefore)(s, steps_1.ProjectStep.PROJECT_IN_PROGRESS));
/**
 * Whether a commercial may abandon a dossier sitting at `step`. Fails closed on
 * a missing or unknown step.
 */
const canSalesmanAbandon = (step) => step != null &&
    exports.SALESMAN_ABANDONMENT_STEPS.includes(step);
exports.canSalesmanAbandon = canSalesmanAbandon;
/**
 * Whether `action` may run while the project is at `step`. Fails closed: an
 * unknown action or an unknown/missing step returns `false`.
 */
const isActionAllowedFromStep = (action, step) => {
    if (step == null)
        return false;
    const allowed = exports.PROJECT_ACTION_ALLOWED_FROM[action];
    if (allowed === undefined)
        return false;
    return allowed.includes(step);
};
exports.isActionAllowedFromStep = isActionAllowedFromStep;
//# sourceMappingURL=action-scopes.js.map